import React, { useEffect, useMemo, useState } from "react";
import { Package, Eye, FileSpreadsheet, Printer, Scissors, Shirt, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatApiError, fmtIDR, fmtDateShort } from "../../lib/api";
import { PageHeader, Panel, StatusBadge } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const PAGE_SIZE = 10;

function Stat({ label, value, tone }) {
  return <div className="bg-white border border-taupe/20 rounded-lg p-3"><div className="text-[11px] text-wmuted">{label}</div><div className={`text-lg font-heading font-bold mt-0.5 ${tone || "text-cocoa"}`}>{value}</div></div>;
}

export default function ProductProductionHistory() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [ppage, setPpage] = useState(1);
  const load = () => api.get("/production/product-history", { params: q ? { q } : {} }).then((r) => { setRows(r.data); setPpage(1); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const cur = Math.min(ppage, pages);
  const pageRows = rows.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
  return (
    <div data-testid="product-history-page">
      <PageHeader title="Riwayat Produksi Produk" subtitle="Semua produk yang pernah diproduksi — kain, order, aksesori & riwayat HPP">
        <div className="flex gap-2">
          <Input placeholder="Cari produk..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-52" data-testid="ph-search" />
          <Button variant="outline" onClick={load} className="border-taupe text-cocoa">Cari</Button>
        </div>
      </PageHeader>
      <Panel>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Produk</TableHead><TableHead className="text-right">Rencana</TableHead><TableHead className="text-right">Order</TableHead>
            <TableHead className="text-right">Passed</TableHead><TableHead className="text-right">Rework</TableHead><TableHead className="text-right">Reject</TableHead>
            <TableHead className="text-right">Kain (yd)</TableHead><TableHead className="text-right">HPP Estimasi</TableHead><TableHead className="text-right">HPP Aktual</TableHead>
            <TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pageRows.map((r) => (
              <TableRow key={r.product_id} data-testid={`ph-row-${r.product_id}`}>
                <TableCell className="font-medium text-cocoa">{r.product_name}</TableCell>
                <TableCell className="text-right">{r.plans_count}</TableCell>
                <TableCell className="text-right">{r.orders_count}</TableCell>
                <TableCell className="text-right text-status-passed font-semibold">{r.passed}</TableCell>
                <TableCell className="text-right text-status-rework">{r.rework}</TableCell>
                <TableCell className="text-right text-status-reject">{r.reject}</TableCell>
                <TableCell className="text-right">{r.total_yard}</TableCell>
                <TableCell className="text-right">{fmtIDR(r.hpp_estimate)}</TableCell>
                <TableCell className="text-right font-semibold">{fmtIDR(r.hpp_actual)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setDetail(r.product_id)} data-testid={`ph-view-${r.product_id}`}><Eye className="w-4 h-4 text-cocoa mr-1" />Lihat</Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={10} className="text-center text-wmuted py-10"><Package className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada produk yang diproduksi.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-2 text-sm">
          <span className="text-wmuted">Hal {cur} / {pages}</span>
          <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={cur <= 1} onClick={() => setPpage(cur - 1)} data-testid="ph-prod-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={cur >= pages} onClick={() => setPpage(cur + 1)} data-testid="ph-prod-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
      <DetailDialog pid={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function DetailDialog({ pid, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (pid) api.get(`/production/product-history/${pid}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [pid]);
  if (!pid) return null;
  const s = d?.summary || {};
  const exportXlsx = () => downloadFile(`/production/product-history/${pid}/export`, {}, `riwayat-produksi-${(s.product_name || "produk")}.xlsx`).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  return (
    <Dialog open={!!pid} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Riwayat Produksi — {s.product_name || "..."}</DialogTitle><DialogDescription className="text-xs text-wmuted">Ringkasan biaya, HPP per varian, rincian batch, serta rekap kain & aksesori.</DialogDescription></DialogHeader>
        {!d ? <div className="py-10 text-center text-wmuted">Memuat...</div> : (
          <div className="space-y-5" data-testid="ph-detail">
            <div className="flex flex-wrap gap-2 justify-end">
              <Button size="sm" onClick={exportXlsx} data-testid="ph-export-xlsx" className="bg-cocoa text-ivory"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Export Excel</Button>
              <Button size="sm" variant="outline" onClick={() => window.print()} className="border-taupe text-cocoa"><Printer className="w-4 h-4 mr-1.5" />Cetak / PDF</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Stat label="Diproduksi (Passed)" value={s.passed} tone="text-status-passed" />
              <Stat label="Rework / Reject" value={`${s.rework} / ${s.reject}`} tone="text-status-rework" />
              <Stat label="HPP Estimasi (BOM)" value={fmtIDR(s.hpp_estimate)} />
              <Stat label="HPP Aktual / pcs" value={fmtIDR(s.hpp_actual)} />
              <Stat label="Total Kain" value={`${s.total_yard} yd`} />
              <Stat label="Biaya Kain" value={fmtIDR(s.costs?.material)} />
              <Stat label="Biaya Aksesori" value={fmtIDR(s.costs?.accessory)} />
              <Stat label="Total Biaya" value={fmtIDR(s.costs?.total)} />
            </div>

            {(d.variant_hpp || []).length > 0 && <Section title="HPP per Varian (kain + overhead jahit/aksesori/cutting)" icon={<Package className="w-4 h-4" />}>
              <Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Varian</TableHead><TableHead>Kain Dipakai</TableHead><TableHead className="text-right">Kain</TableHead><TableHead className="text-right">Overhead</TableHead><TableHead className="text-right">HPP / pcs</TableHead></TableRow></TableHeader>
                <TableBody>{d.variant_hpp.map((v, i) => <TableRow key={i} data-testid={`ph-vhpp-${i}`}><TableCell className="font-medium text-cocoa">{v.color || "-"} / {v.size || "-"}</TableCell><TableCell className="text-xs">{v.fabrics.map((f) => `${f.name} (${f.yard_per_pcs} yd)`).join(" + ") || "-"}</TableCell><TableCell className="text-right">{fmtIDR(v.fabric_cost)}</TableCell><TableCell className="text-right">{fmtIDR(v.overhead)}</TableCell><TableCell className="text-right whitespace-nowrap font-semibold">{fmtIDR(v.hpp)}</TableCell></TableRow>)}</TableBody></Table>
            </Section>}

            <BatchTable batches={d.batches || []} />

            <div className="grid md:grid-cols-2 gap-4">
              <Section title="Rekap Kain (semua batch)" icon={<Scissors className="w-4 h-4" />}>
                <Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Kode</TableHead><TableHead>Kain</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Harga/yd</TableHead><TableHead className="text-right">Total Yard</TableHead><TableHead className="text-right">Biaya</TableHead></TableRow></TableHeader>
                  <TableBody>{(d.fabric_recap || []).map((r, i) => <TableRow key={i} data-testid={`ph-fab-${i}`}><TableCell className="font-mono text-xs">{r.code || "-"}</TableCell><TableCell>{r.name}</TableCell><TableCell className="text-xs">{r.supplier || "-"}</TableCell><TableCell className="text-right whitespace-nowrap">{fmtIDR(r.price)}</TableCell><TableCell className="text-right">{r.used}</TableCell><TableCell className="text-right whitespace-nowrap">{fmtIDR(r.cost)}</TableCell></TableRow>)}
                    {!(d.fabric_recap || []).length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-4">-</TableCell></TableRow>}</TableBody></Table>
              </Section>
              <Section title="Rekap Aksesori (semua batch)" icon={<Package className="w-4 h-4" />}>
                <Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Kode</TableHead><TableHead>Aksesori</TableHead><TableHead>Supplier</TableHead><TableHead className="text-right">Harga/pcs</TableHead><TableHead className="text-right">Total Pcs</TableHead><TableHead className="text-right">Biaya</TableHead></TableRow></TableHeader>
                  <TableBody>{(d.accessory_recap || []).map((r, i) => <TableRow key={i} data-testid={`ph-acc-${i}`}><TableCell className="font-mono text-xs">{r.code || "-"}</TableCell><TableCell>{r.name}</TableCell><TableCell className="text-xs">{r.supplier || "-"}</TableCell><TableCell className="text-right whitespace-nowrap">{fmtIDR(r.price)}</TableCell><TableCell className="text-right">{r.pcs}</TableCell><TableCell className="text-right whitespace-nowrap">{fmtIDR(r.cost)}</TableCell></TableRow>)}
                    {!(d.accessory_recap || []).length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-4">-</TableCell></TableRow>}</TableBody></Table>
              </Section>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BatchTable({ batches }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const filtered = useMemo(() => {
    const ql = query.trim().toLowerCase();
    if (!ql) return batches;
    return batches.filter((b) => `${b.plan_number} ${b.name}`.toLowerCase().includes(ql));
  }, [batches, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const cur = Math.min(page, pages);
  const slice = filtered.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-semibold text-cocoa flex items-center gap-1.5"><Package className="w-4 h-4" />PRODUKSI ({filtered.length})</div>
        <Input placeholder="Cari no/nama rencana..." value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} className="w-56 h-8" data-testid="ph-batch-search" />
      </div>
      <Panel>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead className="w-8"></TableHead><TableHead>No Rencana</TableHead><TableHead>Nama</TableHead><TableHead>Tanggal</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Passed</TableHead>
            <TableHead className="text-right">HPP Aktual</TableHead><TableHead className="text-right">Detail</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {slice.map((b) => {
              const isOpen = open === b.plan_id;
              return (
                <React.Fragment key={b.plan_id}>
                  <TableRow data-testid={`ph-batch-row-${b.plan_id}`} className="cursor-pointer" onClick={() => setOpen(isOpen ? null : b.plan_id)}>
                    <TableCell>{isOpen ? <ChevronDown className="w-4 h-4 text-cocoa" /> : <ChevronRight className="w-4 h-4 text-wmuted" />}</TableCell>
                    <TableCell className="font-mono text-xs">{b.plan_number}</TableCell>
                    <TableCell className="text-cocoa">{b.name}</TableCell>
                    <TableCell className="text-xs text-wmuted">{fmtDateShort(b.created_at)}</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                    <TableCell className="text-right">{b.target_qty}</TableCell>
                    <TableCell className="text-right text-status-passed font-semibold">{b.passed}</TableCell>
                    <TableCell className="text-right">{fmtIDR(b.hpp_actual)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : b.plan_id); }} data-testid={`ph-batch-detail-${b.plan_id}`} className="text-cocoa">{isOpen ? "Tutup" : "Show Detail"}</Button></TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={9} className="bg-ivory/40 p-3" data-testid={`ph-batch-panel-${b.plan_id}`}>
                        <BatchDetail b={b} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {!slice.length && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-8">Tidak ada rencana produksi.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-2 text-sm">
          <span className="text-wmuted">Hal {cur} / {pages}</span>
          <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={cur <= 1} onClick={() => setPage(cur - 1)} data-testid="ph-batch-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={cur >= pages} onClick={() => setPage(cur + 1)} data-testid="ph-batch-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

function BatchDetail({ b }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div>
        <div className="text-[11px] font-semibold text-cocoa uppercase tracking-wide mb-1 flex items-center gap-1"><Scissors className="w-3.5 h-3.5" />Kain Terpakai</div>
        {b.fabrics.length ? b.fabrics.map((f, i) => <div key={i} className="flex justify-between text-xs py-0.5 border-b border-taupe/10"><span>{f.name} <span className="text-wmuted font-mono">{f.lot || ""}</span></span><span>{f.used} yd · {fmtIDR(f.subtotal)}</span></div>) : <div className="text-xs text-wmuted">-</div>}
        <div className="text-[11px] font-semibold text-cocoa uppercase tracking-wide mt-2 mb-1">Aksesori</div>
        {b.accessories.length ? b.accessories.map((a, i) => <div key={i} className="flex justify-between text-xs py-0.5 border-b border-taupe/10"><span>{a.name}</span><span>{a.qty} {a.unit} · {fmtIDR(a.subtotal)}</span></div>) : <div className="text-xs text-wmuted">-</div>}
      </div>
      <div>
        <div className="text-[11px] font-semibold text-cocoa uppercase tracking-wide mb-1 flex items-center gap-1"><Shirt className="w-3.5 h-3.5" />Order Jahit</div>
        {b.orders.length ? b.orders.map((o, i) => <div key={i} className="flex justify-between text-xs py-0.5 border-b border-taupe/10"><span>{o.number} · {o.partner_name}</span><span>{o.total_received}/{o.total_send} · P{o.passed} R{o.rework} X{o.reject}</span></div>) : <div className="text-xs text-wmuted">-</div>}
        <div className="mt-2 text-xs bg-white rounded p-2 space-y-0.5 border border-taupe/15">
          <div className="flex justify-between"><span className="text-wmuted">Biaya Kain</span><span>{fmtIDR(b.costs.material)}</span></div>
          <div className="flex justify-between"><span className="text-wmuted">Biaya Aksesori</span><span>{fmtIDR(b.costs.accessory)}</span></div>
          <div className="flex justify-between"><span className="text-wmuted">Biaya Cutting</span><span>{fmtIDR(b.costs.cutting)}</span></div>
          <div className="flex justify-between"><span className="text-wmuted">Biaya Jahit</span><span>{fmtIDR(b.costs.jahit)}</span></div>
          <div className="flex justify-between font-semibold text-cocoa border-t border-taupe/20 pt-0.5"><span>Total</span><span>{fmtIDR(b.costs.total)}</span></div>
          <div className="flex justify-between text-cocoa"><span className="text-wmuted">HPP Estimasi / Aktual</span><span>{fmtIDR(b.hpp_estimate)} / {fmtIDR(b.hpp_actual)}</span></div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <div className="font-semibold text-cocoa mb-1.5 flex items-center gap-1.5">{icon}{title}</div>
      <Panel>{children}</Panel>
    </div>
  );
}
